// src/services/dataPersistence/dataSync.ts
// Core service for syncing user data between memory, local storage, and remote database

import { TripperUserData, DataSyncOptions, ImageMessage } from './types';
import { saveToLocalStorage, loadFromLocalStorage, debounce } from './utils/storageUtils';


export class DataSyncService {
  private static instance: DataSyncService;
  private userData: TripperUserData;
  private readonly STORAGE_KEY = 'tripper_user_data';
  private autoSaveTimer: number | null = null;
  private options: Required<DataSyncOptions>;
  private isInitialized = false;
  private savePending = false;
  
  // Default options
  private readonly DEFAULT_OPTIONS: Required<DataSyncOptions> = {
    autoSave: true,
    autoSaveInterval: 30000, // 30 seconds
    storageType: 'localStorage'
  };
  

  // Add to DataSyncService class
  private changedEntities: Record<string, Set<string>> = {
    conversation: new Set<string>(),
    locations: new Set<string>(),
    weather: new Set<string>(),
    images: new Set<string>()
  };

  // Get changed entity IDs
  public getChangedEntityIds(type: keyof typeof this.changedEntities): Set<string> {
    return new Set(this.changedEntities[type]);
  }

  // Clear synced entity IDs
  /* public clearChangedEntityIds(type: keyof typeof this.changedEntities, ids: Set<string>): void {
    ids.forEach(id => this.changedEntities[type].delete(id));
  } */
  public clearChangedEntityIds(type: keyof typeof this.changedEntities, ids: Set<string>): void {
    //console.log(`[DataSync] Clearing ${ids.size} ${type} change markers`);
    ids.forEach(id => this.changedEntities[type].delete(id));
    //console.log(`[DataSync] Remaining changed ${type}:`, this.changedEntities[type].size);
  }


  /**
   * Private constructor for singleton pattern
   */
  private constructor(options?: DataSyncOptions) {
    this.options = { ...this.DEFAULT_OPTIONS, ...options };
    this.userData = this.createEmptyData();
    
    // Setup auto-save if enabled
    if (this.options.autoSave) {
      this.setupAutoSave();
    }
  }


  // In DataSyncService class
  public updateData(partialData: Partial<TripperUserData>): void {
    const currentData = this.getAllData();
    const updatedData = {
      ...currentData,
      ...partialData
    };
    //console.log('Data updated here : 1', JSON.stringify(updatedData))
    //this.saveUserData(updatedData); // or
    localStorage.setItem('tripper_user_data', JSON.stringify(updatedData));
  }

   // In DataSyncService class
   public saveUserData(partialData: Partial<TripperUserData>): void {
    const currentData = this.getAllData();
    const updatedData = {
      ...currentData,
      ...partialData
    };
    //console.log('Data updated here : 2', JSON.stringify(updatedData))
    //this.saveUserData(updatedData); // or
    localStorage.setItem('tripper_user_data', JSON.stringify(updatedData));
  }

  /**
   * Get all image messages
   */
  public getImageMessages(): any[] {
    return this.getAllData().images?.messages || [];
  }

  /**
   * Update image messages
   */
  public updateImageMessages_UNUSED(messages: any[]): void {
    // Get current data first
    const currentData = this.getAllData();
    /* console.log('Current image data : --------------------------------------------------------------------', JSON.stringify({
      ...currentData,
      images: {
        messages: messages
      }
    })); */
    // Update with new images collection
    //console.log('Data updated here : 3', JSON.stringify(currentData))
    localStorage.setItem('tripper_user_data', JSON.stringify({
      ...currentData,
      images: {
        messages: messages
      }
    }));
  }

  public updateImageMessages(messages: any[]): void {
    //console.log('[DataSyncService] updateImageMessages called with', messages.length, 'images');

    // Get current data
    const currentData = this.getAllData();
    const oldImages = currentData.images?.messages || [];
    
    //console.log('[DataSyncService] Old images:', oldImages.length);
    //console.log('[DataSyncService] New images:', messages.length);

    // Create updated data object
    const updatedData = {
      ...currentData,
      images: { messages: messages }
    };

    // Track changes
    const oldImagesMap = new Map(oldImages.map(img => [img.id, JSON.stringify(img)]));
    messages.forEach(newImg => {
      const oldImgJson = oldImagesMap.get(newImg.id);
      if (!oldImgJson || oldImgJson !== JSON.stringify(newImg)) {
        //console.log('[DataSyncService] Tracking changed image:', newImg.id);
        this.changedEntities.images.add(newImg.id);
      }
    });

    /* console.log('[DataSyncService] Changed image IDs after tracking:', 
      Array.from(this.changedEntities.images || new Set())
    ); */

    // Update BOTH internal state and localStorage with same data
    this.userData.images = { messages: messages };
    
    //console.log('[DataSyncService] About to save:', updatedData.images.messages.length, 'images');
    localStorage.setItem('tripper_user_data', JSON.stringify(updatedData));

    // Verify save
    const saved = JSON.parse(localStorage.getItem('tripper_user_data') || '{}');
    //console.log('[DataSyncService] Actually saved:', saved.images?.messages?.length, 'images');

    this.markDirty();
    //console.log('[DataSyncService] Image messages updated successfully');
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(options?: DataSyncOptions): DataSyncService {
    if (!DataSyncService.instance) {
      DataSyncService.instance = new DataSyncService(options);
    }
    return DataSyncService.instance;
  }
  
  /**
   * Initialize the service with a user ID
   */
  public initialize(userId: string): void {
    // Set the user ID
    this.userData.userId = userId || 'anonymous';
    this.userData.sessionId = this.generateSessionId();
    this.userData.lastActive = new Date().toISOString();
    
    // Try to load existing data
    this.loadFromLocalStorage();
    
    // Mark as initialized
    this.isInitialized = true;
    
  }
  
  /**
   * Create an empty data structure
   */
  private createEmptyData(): TripperUserData {
    return {
      userId: '',
      sessionId: '',
      lastActive: new Date().toISOString(),
      
      conversation: {
        sequence: []
      },
      
      locations: {
        cards: []
      },
      
      weather: {
        widgets: []
      },
      
      mapState: {
        center: { lat: 40.7128, lng: -74.0060 }, // Default to NYC
        zoom: 10,
        visibleLocationIds: []
      },
      
      itineraries: {
        items: []
      },

      images: { 
        messages: []
      }, 
      
      preferences: {
        uiMode: 'light',
        mapPreference: 'satellite'
      }
    };
  }
  
  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
  
  /**
   * Set up auto-save functionality
   */
  private setupAutoSave(): void {
    // Clear any existing timer
    if (this.autoSaveTimer !== null) {
      window.clearInterval(this.autoSaveTimer);
    }
    
    // Create debounced save function
    const debouncedSave = debounce(() => {
      this.saveToLocalStorage();
    }, this.options.autoSaveInterval);
    
    // Set up auto-save using the timeout method
    this.autoSaveTimer = window.setInterval(() => {
      if (this.savePending) {
        debouncedSave();
        this.savePending = false;
      }
    }, this.options.autoSaveInterval / 2);
  }
  
  /**
   * Mark that changes have been made and save is needed
   */
  private markDirty(): void {
    this.savePending = true;
    this.userData.lastActive = new Date().toISOString();
  }
  
  /**
   * Save data to local storage
   */
  public saveToLocalStorage(): boolean {
    if (!this.isInitialized) {
      console.warn('[DataSyncService] Cannot save: not initialized');
      return false;
    }
    
    //console.log('[TRACE] saveToLocalStorage called from:', new Error().stack);
    //console.log('[DataSyncService] Saving data to localStorage');
    return saveToLocalStorage(this.STORAGE_KEY, this.userData);
  }
  
  /**
   * Load data from local storage
   */
  public loadFromLocalStorage(): boolean {
    const data = loadFromLocalStorage<TripperUserData>(this.STORAGE_KEY);
    
    if (data) {
      // Preserve user ID and session
      const userId = this.userData.userId;
      const sessionId = this.userData.sessionId;
      
      // Update with loaded data
      this.userData = data;
      
      // Restore current user if was anonymous
      if (this.userData.userId === 'anonymous' && userId) {
        this.userData.userId = userId;
        this.userData.sessionId = sessionId;
      }
      
      console.log('[DataSyncService] Loaded data from localStorage');
      return true;
    }
    
    console.log('[DataSyncService] No data found in localStorage');
    return false;
  }
  
  /**
   * Get all user data (for debugging)
   */
  public getAllData(): TripperUserData {
    return this.userData;
  }
  
  /**
   * Get conversation data
   */
  public getConversationData() {
    //console.log('[DataSyncService] Conversation data: ---------------------- ', this.userData.conversation);
    return this.userData.conversation;
  }
  
  /**
   * Update conversation data
   */
  // Update updateConversationData
  public updateConversationData(data: Partial<TripperUserData['conversation']>) {
    // If sequence is updated, track new elements
    if (data.sequence) {
      const existingIds = new Set(this.userData.conversation.sequence.map(e => e.elementId));
      data.sequence.forEach(element => {
        if (!existingIds.has(element.elementId)) {
          this.changedEntities.conversation.add(element.elementId);
        }
      });
    }
    
    this.userData.conversation = { ...this.userData.conversation, ...data };
    this.markDirty();
  }

  
  /**
   * Add an element to the conversation sequence
   */
   // Update addConversationElement method
   public addConversationElement(element: ConversationElement) {
      console.log('[TRACE] addConversationElement called, type:', element.type);

    const sequence = [...this.userData.conversation.sequence, element];
    this.updateConversationData({ sequence });
    // Track changed element
    this.changedEntities.conversation.add(element.elementId);
    console.log('[TRACE] markDirty called from addConversationElement');
  }


    /**
   * Update itineraries data
   */
  public updateItineraries(itineraries: any): void {
    this.updateData({
      itineraries: {
        ...this.getAllData().itineraries,
        ...itineraries
      }
    });
    
    // Notify data update
    window.dispatchEvent(new CustomEvent('tripper-data-updated'));
  }
  
  /**
   * Get messages collection (extracted from sequence)
   */
  public getMessages(): Message[] {
    // Find all message elements in the sequence
    const messageElements = this.userData.conversation.sequence
      .filter(element => element.type === 'message');
    
    // Get the actual message data for each element
    const messages: Message[] = [];
    for (const element of messageElements) {
      const message = this.getMessage(element.elementId);
      if (message) {
        messages.push(message);
      }
    }
    
    return messages;
  }
  
  /**
   * Get a specific message by ID
   */

  private getMessage(messageId: string): Message | undefined {
    const element = this.userData.conversation.sequence
      .find(el => el.type === 'message' && el.elementId === messageId);
    
    if (!element) return undefined;
    
    try {
      // Parse the message and ensure Date objects are properly restored
      const parsed = JSON.parse(element.elementId);
      return {
        ...parsed,
        timestamp: new Date(parsed.timestamp),
        // _extractedData will be included automatically if it exists
      } as Message;
    } catch (e) {
      console.error('Failed to parse message data', e);
      return undefined;
    }
  }
  private getMessage_UNUSED(messageId: string): Message | undefined {
    // This would usually come from a messages collection
    // For now, we'll extract it from the sequence
    const element = this.userData.conversation.sequence
      .find(el => el.type === 'message' && el.elementId === messageId);
    
    if (!element) return undefined;
    
    // Parse the elementId to get the actual message data
    // In a real implementation, we would store messages in their own collection
    try {
      // For now, we're assuming elementId contains the serialized message
      return JSON.parse(element.elementId) as Message;
    } catch (e) {
      console.error('Failed to parse message data', e);
      return undefined;
    }
  }
  
  /**
   * Get location cards
   */
  public getLocationCards() {
    return this.userData.locations.cards;
  }
  
  /**
   * Update location cards
   */
  /* public updateLocationCards(cards: LocationCard[]) {
    this.userData.locations.cards = cards;
    this.markDirty();
  } */

  // In dataSync.ts, update updateLocationCards
  public updateLocationCards(cards: LocationCard[]) {
    // Track which cards are new or changed
    const existingCards = this.userData.locations.cards || [];
    const existingMap = new Map(existingCards.map(card => [card.id, JSON.stringify(card)]));

    console.log('[DataSync] Comparing locations...');

    // Only log first location to avoid console spam
    if (cards.length > 0 && existingCards.length > 0) {
      const testCard = cards[0];
      const existingTest = existingCards.find(c => c.id === testCard.id);
      
      if (existingTest) {
        console.log('[DataSync] DETAILED COMPARISON for first location:');
        console.log('Existing:', JSON.stringify(existingTest, null, 2));
        console.log('New:', JSON.stringify(testCard, null, 2));
        
        // Compare JSON strings
        const existingJson = JSON.stringify(existingTest);
        const newJson = JSON.stringify(testCard);
        console.log('JSON Match:', existingJson === newJson);
        
        if (existingJson !== newJson) {
          console.log('JSON Length - Existing:', existingJson.length, 'New:', newJson.length);
        }
      }
    }

    //console.log('[DataSync] Existing locations:', existingCards.length);
    //console.log('[DataSync] New locations:', cards.length);
    
    // Mark changed or new cards
    /* cards.forEach(card => {
      const existingCardJson = existingMap.get(card.id);
      if (!existingCardJson || existingCardJson !== JSON.stringify(card)) {
        this.changedEntities.locations.add(card.id);
      }
    }); */

    /* cards.forEach(card => {
      const existingCardJson = existingMap.get(card.id);
      const newCardJson = JSON.stringify(card);
      
      if (!existingCardJson || existingCardJson !== newCardJson) {
          //console.log('[DataSync] Location marked as changed:', card.id);
          if (existingCardJson && existingCardJson !== newCardJson) {
            //console.log('[DataSync] Difference detected for:', card.id);
            // You could add more detailed comparison here
          }
          this.changedEntities.locations.add(card.id);
        }
    }); */
    /* cards.forEach(card => {
      const existingCardJson = existingMap.get(card.id);
      const newCardJson = JSON.stringify(card);
      
      if (!existingCardJson || existingCardJson !== newCardJson) {
        // Parse existing to check for 0,0 coordinates
        const existing = existingCardJson ? JSON.parse(existingCardJson) : null;
        
        // Skip if this is just fixing uninitialized coordinates
        if (existing && 
            existing.position?.lat === 0 && 
            existing.position?.lng === 0 && 
            (card.position.lat !== 0 || card.position.lng !== 0)) {
          console.log('[DataSync] Ignoring 0,0 coordinate update for:', card.name);
          return; // Don't mark as changed
        }
        
        this.changedEntities.locations.add(card.id);
      }
    }); */

    cards.forEach(card => {
      const existingCardJson = existingMap.get(card.id);
      const newCardJson = JSON.stringify(card);
      if (!existingCardJson || existingCardJson !== newCardJson) {
        //console.log('[DataSync] Location marked as changed:', card.position);
        console.log('[DataSync] ---- Comparing card: ');
        console.log(' Existing ', existingCardJson);
        console.log(' New Card ', newCardJson);
        // If the INCOMING card has 0,0, don't mark it as changed
        if (card.position?.lat === 0 && card.position?.lng === 0) {
          console.log('[DataSync] Skipping 0,0 location:', card.name);
          return; // Don't mark as changed
        }
        
        this.changedEntities.locations.add(card.id);
      }
    });

    //console.log('[DataSync] Total changed locations:', this.changedEntities.locations.size);

    this.userData.locations.cards = cards;
    this.markDirty();
  }
  
  /**
   * Get weather widgets
   */
  public getWeatherWidgets() {
    return this.userData.weather.widgets;
  }
  
  /**
   * Update weather widgets
   */
  public updateWeatherWidgets(widgets: WeatherWidget[]) {
    this.userData.weather.widgets = widgets;
    this.markDirty();
  }
  
  /**
   * Get map state
   */
  public getMapState() {
    return this.userData.mapState;
  }
  
  /**
   * Update map state
   */
  public updateMapState(mapState: Partial<TripperUserData['mapState']>) {
    this.userData.mapState = { ...this.userData.mapState, ...mapState };
    this.markDirty();
  }
  
  /**
   * Get all itineraries
   */
  public getItineraries() {
    return this.userData.itineraries.items;
  }
  
  /**
   * Get current itinerary
   */
  public getCurrentItinerary() {
    const currentId = this.userData.itineraries.currentItineraryId;
    if (!currentId) return undefined;
    
    return this.userData.itineraries.items.find(item => item.id === currentId);
  }
  
  /**
   * Add or update an itinerary
   */
  public saveItinerary(itinerary: Itinerary) {

    // Create a stack trace to see where this is called from
    const stackTrace = new Error().stack || '';
    const caller = stackTrace.split('\n')[2] || 'unknown';

    //console.group(`[DataSync] Saving itinerary ${itinerary.id}`);
    //console.log('Called from:', caller);
    //console.log('Empty days?', !itinerary.days || itinerary.days.length === 0);
    //console.log('Current count:', this.getItineraries().length);
    //console.groupEnd();

    /* console.log('[DataSyncService] Saving itinerary:', {
      id: itinerary.id,
      isNew: !this.getItineraries().some(i => i.id === itinerary.id),
      hasDays: itinerary.days?.length > 0,
      calledFrom: new Error().stack?.split('\n')[2]?.trim() || 'unknown'
    }); */

    const items = [...this.userData.itineraries.items];
    const index = items.findIndex(item => item.id === itinerary.id);
    
    if (index >= 0) {
      // Update existing
      items[index] = { 
        ...itinerary,
        updatedAt: new Date().toISOString()
      };
    } else {
      // Add new
      items.push({
        ...itinerary,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    this.userData.itineraries = {
      ...this.userData.itineraries,
      items,
      currentItineraryId: itinerary.id
    };
    
    this.markDirty();
  }
  
  /**
   * Set the current active itinerary
   */
  public setCurrentItinerary(itineraryId: string) {
    this.userData.itineraries.currentItineraryId = itineraryId;
    this.markDirty();
  }
  
  /**
   * Get user preferences
   */
  public getUserPreferences() {
    return this.userData.preferences;
  }
  

  // Add this public method to DataSyncService class
  public clearAllChangedEntities(): void {
    console.log('[DataSync] Clearing all change tracking');
    
    Object.keys(this.changedEntities).forEach(entityType => {
      const count = this.changedEntities[entityType].size;
      if (count > 0) {
        console.log(`[DataSync] Clearing ${count} ${entityType} changes`);
        this.changedEntities[entityType].clear();
      }
    });
  }

  /**
   * Update user preferences
   */
  // In updateUserPreferences method
  public updateUserPreferences(preferences: Partial<TripperUserData['preferences']>) {
    // Ensure required fields have defaults if undefined
    const updatedPreferences = {
      ...this.userData.preferences,
      ...preferences,
      // Ensure uiMode is never undefined
      uiMode: preferences.uiMode || this.userData.preferences.uiMode || 'light'
    };
    
    this.userData.preferences = updatedPreferences;
    this.markDirty();
    
    // Track changes
    this.changedEntities.preferences.add('preferences');
  }
  
  /**
   * Clear all stored data
   */
  public clearAllData(): void {
    this.userData = this.createEmptyData();
    
    // Re-initialize with the current user ID
    if (this.isInitialized) {
      this.initialize(this.userData.userId);
    }
    
    // Save the empty data to storage
    this.saveToLocalStorage();
    console.log('[DataSyncService] All data cleared');
  }
}
